/**
 * Phase 349 — Building "Emails" tab.
 *
 * Send-only UI: the user picks one of the global templates, edits recipients,
 * optionally attaches files and/or auto-generates the elevator statement for
 * a selected period, then reviews a final preview before the actual Brevo
 * call. Template authoring is done centrally at Settings → Email Templates.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';

// Phase 713 — Sanitize stored email HTML before rendering it inside the
// History / Final Preview dialogs so the preview is pixel-identical to
// what Gmail / Outlook display, while blocking XSS payloads. We keep
// inline `style` + `class` + every formatting tag (tables, lists, images,
// headings, color/font spans, Quill alignment/indent classes) and only
// strip dangerous targets like <script>, event handlers, <iframe>, etc.
const SANITIZE_CFG = {
  ADD_ATTR: ['target', 'rel', 'style', 'class', 'align', 'valign', 'colspan', 'rowspan', 'width', 'height', 'cellspacing', 'cellpadding', 'border', 'bgcolor', 'color', 'face', 'size'],
  ADD_TAGS: ['font'],
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'meta', 'link'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onsubmit', 'onchange'],
  ALLOW_DATA_ATTR: false,
};
const sanitizeEmailHtml = (html) => DOMPurify.sanitize(String(html || ''), SANITIZE_CFG);
import {
  Envelope, PaperPlaneRight, Eye, Star, Paperclip, Trash, ClockCounterClockwise, ArrowsClockwise, MagnifyingGlass, CaretDown,
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { useLanguage } from '../context/LanguageContext';
import BuildingRecipientsPicker from './BuildingRecipientsPicker';
import RichEmailEditor from './RichEmailEditor';
import { getPdfBlobUrl } from '../utils/pdfUtils';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatBytes = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

export default function BuildingEmailsTab({ buildingId, buildingName }) {
  const { t } = useLanguage();

  // Template catalogue + selection.
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [savingSelection, setSavingSelection] = useState(false);

  // Recipients.
  const [sendTo, setSendTo] = useState('');
  const [sendCc, setSendCc] = useState('');
  const [sendBcc, setSendBcc] = useState('');

  // Phase 359 — Apartments recipients picker. Shared modal lets the user
  // tick which apartments / email addresses receive the email.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState('to'); // 'to' | 'cc' | 'bcc'
  const openPicker = (target) => { setPickerTarget(target); setPickerOpen(true); };
  const onPickerConfirm = (emails) => {
    // Replace the target field with the user's explicit selection. Dedupe
    // case-insensitively but keep the original casing.
    const arr = Array.from(new Set(emails.filter(Boolean)));
    const joined = arr.join(', ');
    if (pickerTarget === 'to') setSendTo(joined);
    else if (pickerTarget === 'cc') setSendCc(joined);
    else if (pickerTarget === 'bcc') setSendBcc(joined);
  };

  // Phase 355 — Editable draft. Subject + body are pre-filled from the
  // selected template (or from a history row when resending) but the user
  // can edit them freely before sending. Templates are no longer required
  // for sending — a typed subject/body is enough.
  const [draftSubject, setDraftSubject] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [draftTouched, setDraftTouched] = useState(false);
  // Phase 368 — Tracks whether the initial template→draft sync has happened.
  // Without this, Quill's mount-time onChange (which fires with the normalized
  // empty string `<p><br></p>`) flips `draftTouched=true` BEFORE the
  // useEffect that loads the template can run, leaving Subject/Body empty.
  const draftHydratedRef = useRef(false);
  // Phase 368 — Attachments collapsed by default to save vertical space.
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);

  // Attachments.
  const [attachments, setAttachments] = useState([]);

  // Elevator statement auto-attach.
  const [attachElev, setAttachElev] = useState(false);
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');

  // Phase 352 — Annual Financial Statement auto-attach.
  const [availableYears, setAvailableYears] = useState([]);
  const [attachAnnual, setAttachAnnual] = useState(false);
  const [annualYear, setAnnualYear] = useState('');

  // Phase 352 — Repairs Statement auto-attach (dynamic columns).
  // Phase 353 — Distribution-per-apartment view. Each row = (repair, apartment).
  const REPAIR_COL_KEYS = [
    'apartment_number', 'owner_name', 'invoice_number',
    'allocation_pct', 'allocated_amount', 'paid_amount', 'remaining',
    'payment_status', 'repair_description', 'repair_date', 'notes',
  ];
  const REPAIR_COL_LABELS = {
    apartment_number: 'Apartment #',
    owner_name: 'Owner Name',
    invoice_number: 'Invoice #',
    allocation_pct: 'Allocation %',
    allocated_amount: 'Allocated Amount',
    paid_amount: 'Paid Amount',
    remaining: 'Remaining Balance',
    payment_status: 'Payment Status',
    repair_description: 'Repair Description',
    repair_date: 'Repair Date',
    notes: 'Notes',
  };
  const [attachRepairs, setAttachRepairs] = useState(false);
  // Phase 355 — Multi-select repairs (replaces year-from / year-to range).
  const [repairsOptions, setRepairsOptions] = useState([]);
  const [repairsSelectedIds, setRepairsSelectedIds] = useState([]);
  const [repairsCols, setRepairsCols] = useState([
    'apartment_number', 'owner_name', 'invoice_number',
    'allocation_pct', 'allocated_amount', 'paid_amount',
    'remaining', 'payment_status',
  ]);

  // Phase 352 — Apartments Balances auto-attach.
  const APT_COL_KEYS = ['apartment', 'owner', 'total_due', 'total_paid', 'remaining_balance'];
  const APT_COL_LABELS = {
    apartment: 'Apartment',
    owner: 'Owner',
    total_due: 'Total Due',
    total_paid: 'Total Paid',
    remaining_balance: 'Remaining Balance',
  };
  const [attachApts, setAttachApts] = useState(false);
  const [aptCols, setAptCols] = useState([...APT_COL_KEYS]);
  // Phase 397 — Optional date range scoping for the Apartments Balances PDF.
  // Empty strings = default "full history / current balances" behavior.
  const [aptPeriodFrom, setAptPeriodFrom] = useState('');
  const [aptPeriodTo, setAptPeriodTo] = useState('');

  // Phase 506 — Elevator Certification attachments. Users pick individual
  // files (PDF reports, photos, certificates, invoices) from across all
  // certifications of this building. State = array of "{cert_id}:{file_id}"
  // tokens; the lookup list (`elevCertFiles`) is loaded once per dialog
  // open and shows the certificate number + file name in the picker.
  const [elevCertFiles, setElevCertFiles] = useState([]);
  const [selectedElevCertFiles, setSelectedElevCertFiles] = useState([]);

  // Phase 352 — Resend support. When user clicks Resend in History the
  // original attachments are auto-re-attached server-side via `resend_log_id`.
  const [resendLogId, setResendLogId] = useState('');
  const [resendOriginals, setResendOriginals] = useState([]);

  // Confirm preview modal.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmSubject, setConfirmSubject] = useState('');
  const [confirmBody, setConfirmBody] = useState('');
  // Phase 389 — `confirmBodyHtml` mirrors the EXACT normalized HTML that will
  // be sent to Brevo (post `<p>` → `<div>` rewrite) so the preview dialog
  // is byte-identical to what Gmail will receive.
  const [confirmBodyHtml, setConfirmBodyHtml] = useState('');
  const [sending, setSending] = useState(false);

  // ------------------------------------------------------------------
  // Phase 982 — Working-draft auto-save
  //
  // Persists the compose form to localStorage on every relevant change
  // and re-hydrates it when the user comes back to Building Management
  // → Email Center (e.g. after navigating away to grab a file).
  //
  // Key is scoped by buildingId so different buildings keep independent
  // drafts. Attachments that are still File objects in memory (raw
  // uploads via <input type="file">) can't be serialized to
  // localStorage — everything else (recipients, template selection,
  // subject / body, every attachment toggle & selection list, resend
  // metadata) is preserved.
  // ------------------------------------------------------------------
  const DRAFT_STORAGE_KEY = `buildingemails_working_draft_${buildingId}_v1`;
  const draftHydratingRef = useRef(false);
  const restoredOnceRef = useRef(false);

  const collectDraftSnapshot = useCallback(() => ({
    selectedId,
    sendTo, sendCc, sendBcc,
    draftSubject, draftBody, draftTouched,
    attachElev, periodFrom, periodTo,
    attachAnnual, annualYear,
    attachRepairs, repairsSelectedIds, repairsCols,
    attachApts, aptCols, aptPeriodFrom, aptPeriodTo,
    selectedElevCertFiles,
    resendLogId,
    saved_at: new Date().toISOString(),
  }), [
    selectedId, sendTo, sendCc, sendBcc,
    draftSubject, draftBody, draftTouched,
    attachElev, periodFrom, periodTo,
    attachAnnual, annualYear,
    attachRepairs, repairsSelectedIds, repairsCols,
    attachApts, aptCols, aptPeriodFrom, aptPeriodTo,
    selectedElevCertFiles,
    resendLogId,
  ]);

  const hasDraftContent = (s) => !!(s && (
    (s.sendTo || '').trim() ||
    (s.sendCc || '').trim() ||
    (s.sendBcc || '').trim() ||
    (s.draftSubject || '').trim() ||
    (s.draftBody || '').trim() ||
    (s.selectedId && (s.draftTouched || (s.draftSubject || '').trim() || (s.draftBody || '').trim())) ||
    s.attachElev || s.attachAnnual || s.attachRepairs || s.attachApts ||
    (Array.isArray(s.repairsSelectedIds) && s.repairsSelectedIds.length > 0) ||
    (Array.isArray(s.selectedElevCertFiles) && s.selectedElevCertFiles.length > 0) ||
    s.resendLogId
  ));

  const clearWorkingDraft = useCallback(() => {
    try { localStorage.removeItem(DRAFT_STORAGE_KEY); } catch (_e) { /* ignore */ }
  }, [DRAFT_STORAGE_KEY]);

  // Restore once per (buildingId, mount).
  useEffect(() => {
    if (restoredOnceRef.current) return;
    restoredOnceRef.current = true;
    try {
      const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (!raw) return;
      const s = JSON.parse(raw);
      if (!hasDraftContent(s)) { clearWorkingDraft(); return; }
      draftHydratingRef.current = true;
      if (s.selectedId) setSelectedId(s.selectedId);
      if (typeof s.sendTo === 'string') setSendTo(s.sendTo);
      if (typeof s.sendCc === 'string') setSendCc(s.sendCc);
      if (typeof s.sendBcc === 'string') setSendBcc(s.sendBcc);
      if (typeof s.draftSubject === 'string') setDraftSubject(s.draftSubject);
      if (typeof s.draftBody === 'string') setDraftBody(s.draftBody);
      if (typeof s.draftTouched === 'boolean') setDraftTouched(s.draftTouched);
      if (typeof s.attachElev === 'boolean') setAttachElev(s.attachElev);
      if (typeof s.periodFrom === 'string') setPeriodFrom(s.periodFrom);
      if (typeof s.periodTo === 'string') setPeriodTo(s.periodTo);
      if (typeof s.attachAnnual === 'boolean') setAttachAnnual(s.attachAnnual);
      if (typeof s.annualYear === 'string') setAnnualYear(s.annualYear);
      if (typeof s.attachRepairs === 'boolean') setAttachRepairs(s.attachRepairs);
      if (Array.isArray(s.repairsSelectedIds)) setRepairsSelectedIds(s.repairsSelectedIds);
      if (Array.isArray(s.repairsCols)) setRepairsCols(s.repairsCols);
      if (typeof s.attachApts === 'boolean') setAttachApts(s.attachApts);
      if (Array.isArray(s.aptCols)) setAptCols(s.aptCols);
      if (typeof s.aptPeriodFrom === 'string') setAptPeriodFrom(s.aptPeriodFrom);
      if (typeof s.aptPeriodTo === 'string') setAptPeriodTo(s.aptPeriodTo);
      if (Array.isArray(s.selectedElevCertFiles)) setSelectedElevCertFiles(s.selectedElevCertFiles);
      if (typeof s.resendLogId === 'string') setResendLogId(s.resendLogId);
      // Mark editor as hydrated so the Quill mount-time onChange does not
      // flip `draftTouched` back and forth.
      draftHydratedRef.current = true;
      setTimeout(() => { draftHydratingRef.current = false; }, 0);
    } catch (_e) { /* corrupt storage — ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId]);

  // Persist every relevant change.
  useEffect(() => {
    if (draftHydratingRef.current) return;
    try {
      const snap = collectDraftSnapshot();
      if (hasDraftContent(snap)) {
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(snap));
      } else {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
      }
    } catch (_e) { /* quota / disabled storage — ignore */ }
  }, [collectDraftSnapshot, DRAFT_STORAGE_KEY]);

  const discardBuildingDraft = () => {
    clearWorkingDraft();
    setSendTo(''); setSendCc(''); setSendBcc('');
    setSelectedId('');
    setDraftSubject(''); setDraftBody(''); setDraftTouched(false);
    setAttachElev(false); setPeriodFrom(''); setPeriodTo('');
    setAttachAnnual(false);
    setAttachRepairs(false); setRepairsSelectedIds([]);
    setAttachApts(false); setAptPeriodFrom(''); setAptPeriodTo('');
    setResendLogId(''); setResendOriginals([]);
    setSelectedElevCertFiles([]);
    setAttachments([]);
    try {
      const { toast } = require('sonner');
      toast.success(t('draftDiscarded') || 'Draft discarded');
    } catch (_e) { /* ignore */ }
  };


  // Phase 391 — Per-attachment preview state. Each kind tracks its own
  // loading flag so multiple previews can run in parallel without
  // contention. `attachmentPreviewLoading[kind]` is true while the
  // generator + blob fetch are in-flight.
  const [attachmentPreviewLoading, setAttachmentPreviewLoading] = useState({});
  // Phase 511 — In-app PDF preview modal.
  //
  // Previous attempts:
  //   1) `window.open(blobUrl, '_blank')` after async fetch → blocked by
  //      popup blockers + dies inside the Capacitor webview.
  //   2) `<iframe src={blobUrl}>` after axios fetch → modern Chrome and the
  //      Capacitor webview both refuse to render `blob:` PDFs inside an
  //      iframe, showing only a placeholder with the blob ID and an "Open"
  //      button (the user-reported "black screen with random file id" bug).
  //
  // Fix: point the iframe DIRECTLY at the backend URL. The endpoints already
  // return `application/pdf` with `Content-Disposition: inline`, and the
  // browser's native PDF viewer renders that perfectly inside an iframe.
  // Auth is carried via `?token=...` query param (supported by
  // get_current_user in server.py). No blob, no async, no popup, no
  // placeholder — the modal mounts the iframe with a real URL on the first
  // click and the PDF appears immediately.
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [pdfPreviewTitle, setPdfPreviewTitle] = useState('');
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);

  const openAttachmentPreview = useCallback(async (kind, params = {}) => {
    // Phase 735.11 — Fetch as a Blob URL via cookie auth so browser
    // extensions / adblockers cannot intercept the request (no `?token=`
    // query string in the iframe src).
    // Phase 920.14 — Neutral path `/files/preview/…` because ad-blockers
    // treat any URL containing `email-attachments` (or `attachment` in
    // general) as trackable content and return ERR_BLOCKED_BY_CLIENT.
    const path = `/buildings/${buildingId}/files/preview/${kind}`;
    try {
      const blobUrl = await getPdfBlobUrl(path, { ...params, disposition: 'inline' });
      setPdfPreviewUrl(blobUrl);
      setPdfPreviewTitle(kind.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
      setPdfPreviewOpen(true);
    } catch (err) {
      try {
        const { toast } = await import('sonner');
        toast.error(err?.message || 'Failed to load attachment');
      } catch (_e) { /* ignore */ }
    }
  }, [buildingId]);

  // Preview helper for already-uploaded files (Elevator Certification, etc.)
  // Phase 735.11 — Same Blob-URL pipeline so preview works behind adblockers
  // and inside the Capacitor wrapper.
  const previewExistingFile = useCallback(async (downloadUrl, displayName = 'Attachment') => {
    try {
      const blobUrl = await getPdfBlobUrl(downloadUrl, {});
      setPdfPreviewUrl(blobUrl);
      setPdfPreviewTitle(displayName);
      setPdfPreviewOpen(true);
    } catch (err) {
      try {
        const { toast } = await import('sonner');
        toast.error(err?.message || 'Failed to load file');
      } catch (_e) { /* ignore */ }
    }
  }, []);

  // Phase 351 — Per-building email history. Reuses the global
  // /api/email-history endpoint with a building filter so the audit log is
  // scoped to the current building.
  const [activeView, setActiveView] = useState('compose'); // 'compose' | 'history'
  const [history, setHistory] = useState([]);
  const [historySearch, setHistorySearch] = useState('');
  const [viewEntry, setViewEntry] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);

  const fetchHistory = useCallback(async () => {
    if (!buildingId) return;
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/email-history?limit=200`);
      setHistory(Array.isArray(res.data) ? res.data : []);
    } catch {
      setHistory([]);
    }
  }, [buildingId]);
  useEffect(() => { if (activeView === 'history') fetchHistory(); }, [activeView, fetchHistory]);

  const filteredHistory = useMemo(() => {
    const q = historySearch.trim().toLowerCase();
    if (!q) return history;
    return history.filter(r => {
      const subject = String(r.subject || '').toLowerCase();
      const to = String(r.to_email || (r.to_list || []).join(', ')).toLowerCase();
      const name = String(r.template_name || '').toLowerCase();
      return subject.includes(q) || to.includes(q) || name.includes(q);
    });
  }, [history, historySearch]);

  const formatDateTime = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, '0');
      const mn = String(d.getMinutes()).padStart(2, '0');
      return `${dd}/${mm}/${yyyy} ${hh}:${mn}`;
    } catch { return iso; }
  };

  const openHistoryView = async (row) => {
    setViewEntry({ ...row, body: '', subject: row.subject || '', loading: true });
    setViewOpen(true);
    try {
      const r = await axios.get(`${API}/email-history/${row.log_id}`);
      setViewEntry({ ...r.data, loading: false });
    } catch {
      setViewEntry({ ...row, loading: false, error: 'Failed to load details' });
    }
  };

  // Resend pre-fills the composer with the historical recipients + subject/body.
  // Phase 352 — Also pulls the original attachments list (no binary) so the UI
  // can show them as "to be re-attached" and the backend re-attaches them
  // exactly via `resend_log_id`.
  // Phase 355 — Pre-fills the editable draft (subject + body) with the
  // historical values so the user can edit them before sending again.
  const resendFromHistory = async (row) => {
    setSendTo((row.to_list || []).join(', ') || row.to_email || '');
    setSendCc((row.cc_list || []).join(', ') || row.cc || '');
    setSendBcc((row.bcc_list || []).join(', ') || row.bcc || '');
    if (row.template_id) setSelectedId(row.template_id);
    // Phase 368 — Prefer the rich Quill HTML body (`body_quill`) if present
    // so the editor restores WYSIWYG paragraphs / bold / lists / colors.
    // Fall back to the legacy flattened `body` for emails sent before
    // Phase 368 (those will still load as plain text — best-effort).
    setDraftSubject(row.subject || '');
    setDraftBody(row.body_quill || row.body || '');
    setDraftTouched(true);
    // Phase 368 — Mark the draft as hydrated so the editor's mount-time
    // onChange (firing with the normalized body) does not flip `touched`
    // back-and-forth in a weird state.
    draftHydratedRef.current = true;
    // Phase 368 — If the list row didn't include `body_quill` (older logs
    // or summary-only payload), fetch the full row to get the rich body.
    if (row.log_id && !row.body_quill) {
      try {
        const r = await axios.get(`${API}/email-history/${row.log_id}`);
        if (r.data?.body_quill) {
          setDraftBody(r.data.body_quill);
        }
      } catch { /* ignore */ }
    }
    // Clear fresh auto-toggles — the originals from the previous email will be
    // re-attached automatically by the backend.
    setAttachElev(false); setPeriodFrom(''); setPeriodTo('');
    setAttachAnnual(false);
    setAttachRepairs(false); setRepairsSelectedIds([]);
    setAttachApts(false); setAptPeriodFrom(''); setAptPeriodTo('');
    setAttachments([]);
    setActiveView('compose');
    setViewOpen(false);
    setResendLogId(row.log_id || '');
    // Use the attachments_meta already returned from the history list to
    // populate the originals preview immediately; fetch the detail row to
    // make sure we have an up-to-date list when the user re-opened the
    // dialog before clicking Resend.
    const initialMeta = Array.isArray(row.attachments_meta) ? row.attachments_meta : [];
    setResendOriginals(initialMeta);
    if (!initialMeta.length && row.log_id) {
      try {
        const r = await axios.get(`${API}/email-history/${row.log_id}`);
        const meta = Array.isArray(r.data?.attachments_meta) ? r.data.attachments_meta : [];
        setResendOriginals(meta);
      } catch { /* ignore */ }
    }
    toast.success(t('readyToResend') || 'Draft loaded — edit subject, body, recipients or attachments and click Send.');
  };

  // Phase 352 — Drop the resend reference (originals will NOT be re-attached).
  const clearResend = () => {
    setResendLogId('');
    setResendOriginals([]);
  };

  // Load templates + currently-selected template id for this building.
  const refresh = useCallback(async () => {
    try {
      const [tplRes, selRes, yearsRes, repairsRes] = await Promise.all([
        axios.get(`${API}/email-templates`),
        axios.get(`${API}/buildings/${buildingId}/selected-email-template`),
        axios.get(`${API}/buildings/${buildingId}/email-helpers/years`),
        axios.get(`${API}/buildings/${buildingId}/repairs-options`),
      ]);
      const rows = Array.isArray(tplRes.data) ? tplRes.data : [];
      setTemplates(rows);
      // Phase 369 — Per user request: do NOT auto-select any template on
      // load. The picker stays on the empty "Select template…" placeholder
      // until the user explicitly chooses one. We still keep the
      // selected-email-template endpoint call (used elsewhere by the page)
      // but no longer hydrate `selectedId` from it.
      const yrs = Array.isArray(yearsRes.data?.years) ? yearsRes.data.years : [];
      setAvailableYears(yrs);
      if (yrs.length > 0 && !annualYear) setAnnualYear(String(yrs[0]));
      const reps = Array.isArray(repairsRes.data?.repairs) ? repairsRes.data.repairs : [];
      setRepairsOptions(reps);

      // Phase 506 — Flatten elevator-cert attachments for this building
      // into a flat picker list. Each row: {token, label, size_bytes}.
      try {
        const certRes = await axios.get(`${API}/buildings/${buildingId}/elevator-certifications`);
        const certs = Array.isArray(certRes.data) ? certRes.data : [];
        const out = [];
        for (const c of certs) {
          for (const a of (c.attachments || [])) {
            out.push({
              token: `${c.cert_id}:${a.file_id}`,
              cert_id: c.cert_id,
              certificate_number: c.certificate_number || '',
              inspection_date: c.inspection_date || '',
              file_name: a.name || '',
              size_bytes: a.size_bytes || 0,
            });
          }
        }
        setElevCertFiles(out);
      } catch { /* best effort */ }
    } catch (e) {
      toast.error(t('errorOccurred') || 'Error');
    }
    // eslint-disable-next-line
  }, [buildingId, t]);
  useEffect(() => { refresh(); }, [refresh]);

  const selectedTemplate = useMemo(
    () => templates.find(r => r.template_id === selectedId) || null,
    [templates, selectedId],
  );

  // Phase 355 — Sync the editable draft with the selected template (unless
  // the user has manually edited it, or we're currently resending — in both
  // cases keep what's already on screen so we don't blow away their work).
  useEffect(() => {
    if (resendLogId) return;
    // Phase 368 — Block sync only once the user has truly touched the draft
    // (post-hydration). The pre-hydration Quill onChange (`<p><br></p>`)
    // is ignored, so the first template→draft sync always runs.
    if (draftHydratedRef.current && draftTouched) return;
    setDraftSubject(selectedTemplate?.subject || '');
    setDraftBody(selectedTemplate?.body || '');
    if (selectedTemplate) draftHydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, resendLogId]);

  const persistSelection = async (templateId) => {
    setSavingSelection(true);
    try {
      await axios.put(`${API}/buildings/${buildingId}/selected-email-template`, {
        template_id: templateId,
      });
      toast.success(t('savedSuccess') || 'Saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('errorOccurred') || 'Error');
    } finally {
      setSavingSelection(false);
    }
  };

  // Attachment helpers.
  const addAttachments = (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    setAttachments(prev => [...prev, ...arr.map(f => ({ file: f, name: f.name, size: f.size, type: f.type }))]);
  };
  const removeAttachment = (idx) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  // Stage 1 → Stage 2: render placeholders and open preview.
  // Phase 355 — Template is no longer required; a typed subject/body works
  // too. Resend flows that no longer have access to the original template
  // will still send the user-edited draft.
  const openConfirm = async () => {
    if (!draftSubject.trim() && !draftBody.trim()) {
      toast.error(t('subjectOrBodyRequired') || 'Please enter a subject or body before sending');
      return;
    }
    if (!sendTo.trim()) {
      toast.error(t('atLeastOneRecipient') || 'At least one recipient is required');
      return;
    }
    if (attachElev) {
      if (!periodFrom || !periodTo) {
        toast.error(t('selectPeriodFirst') || 'Please select From and To dates');
        return;
      }
      if (periodFrom > periodTo) {
        toast.error(t('invalidPeriod') || 'From date must be on or before To date');
        return;
      }
    }
    if (attachAnnual && !annualYear) {
      toast.error(t('selectYearFirst') || 'Please select a year for the Annual Financial Statement');
      return;
    }
    if (attachRepairs && repairsCols.length === 0) {
      toast.error(t('selectAtLeastOneColumn') || 'Select at least one column for the Repairs Statement');
      return;
    }
    // Phase 406 — Ticking "Attach Repairs Statement" without selecting any
    // specific repair must NOT silently attach every shared repair.
    if (attachRepairs && repairsSelectedIds.length === 0) {
      toast.error(t('selectAtLeastOneRepair') || 'Select at least one repair to include in the Repairs Statement, or untick the option.');
      return;
    }
    if (attachApts && aptCols.length === 0) {
      toast.error(t('selectAtLeastOneColumn') || 'Select at least one column for the Apartments Balances');
      return;
    }
    setConfirmLoading(true);
    setConfirmOpen(true);
    try {
      // Phase 368 — `preserve_html: true` keeps the full Quill HTML so the
      // Preview shows exactly what's in the editor (paragraphs, lists,
      // bold/italic/colour, alignment). The send step continues to flatten.
      const res = await axios.post(`${API}/buildings/${buildingId}/email-templates/render`, {
        subject: draftSubject || '',
        body: draftBody || '',
        // Phase 387 — Pass the selected statement period so {start_date}
        // and {end_date} placeholders resolve in the subject + body. Dates
        // are converted to DD/MM/YYYY format which matches the rest of the
        // app's display style.
        custom_fields: {
          start_date: periodFrom ? periodFrom.split('-').reverse().join('/') : '',
          end_date: periodTo ? periodTo.split('-').reverse().join('/') : '',
        },
        preserve_html: true,
      });
      setConfirmSubject(res.data?.subject || '');
      setConfirmBody(res.data?.body || '');
      // Phase 389 — Render the SAME `<div>` block HTML that Brevo will
      // transmit, so the preview matches Gmail's rendering exactly.
      setConfirmBodyHtml(res.data?.body_html || res.data?.body || '');
    } catch (e) {
      // Falling back to raw draft text when the render endpoint fails keeps
      // resend usable even if the placeholder service is temporarily down.
      setConfirmSubject(draftSubject || '');
      setConfirmBody(draftBody || '');
      setConfirmBodyHtml(draftBody || '');
    } finally {
      setConfirmLoading(false);
    }
  };

  // Stage 2 → POST (multipart). Includes attach_elevator_statement flag +
  // the chosen period so the backend auto-generates + attaches the PDF.
  const confirmAndSend = async () => {
    if (sending) return;
    setSending(true);
    try {
      const toArr = sendTo.split(',').map(s => s.trim()).filter(Boolean);
      const ccArr = sendCc.split(',').map(s => s.trim()).filter(Boolean);
      const bccArr = sendBcc.split(',').map(s => s.trim()).filter(Boolean);
      const form = new FormData();
      form.append('payload', JSON.stringify({
        subject: confirmSubject,
        body: confirmBody,
        to: toArr,
        cc: ccArr,
        bcc: bccArr,
        template_id: draftTouched ? '' : (selectedTemplate?.template_id || ''),
        template_name: draftTouched ? '' : (selectedTemplate?.name || ''),
      }));
      attachments.forEach(a => { if (a?.file) form.append('attachments', a.file, a.name); });
      form.append('attach_elevator_statement', attachElev ? 'true' : 'false');
      if (attachElev) {
        form.append('elevator_period_from', periodFrom);
        form.append('elevator_period_to', periodTo);
      }
      // Phase 352 — Annual Financial Statement.
      form.append('attach_annual_financial', attachAnnual ? 'true' : 'false');
      if (attachAnnual) form.append('annual_financial_year', String(annualYear || ''));
      // Phase 352 — Repairs Statement (Phase 355: multi-select repair IDs).
      form.append('attach_repairs_statement', attachRepairs ? 'true' : 'false');
      if (attachRepairs) {
        form.append('repairs_ids', repairsSelectedIds.join(','));
        form.append('repairs_columns', repairsCols.join(','));
      }
      // Phase 352 — Apartments Balances.
      form.append('attach_apartments_balances', attachApts ? 'true' : 'false');
      if (attachApts) {
        form.append('apartments_balances_columns', aptCols.join(','));
        // Phase 397 — optional period scoping.
        if (aptPeriodFrom) form.append('apartments_balances_date_from', aptPeriodFrom);
        if (aptPeriodTo) form.append('apartments_balances_date_to', aptPeriodTo);
      }
      // Phase 352 — Resend originals reference.
      if (resendLogId) form.append('resend_log_id', resendLogId);
      // Phase 506 — Manually-selected Elevator Certification attachments.
      if (selectedElevCertFiles.length > 0) {
        form.append('elev_cert_attachment_refs', selectedElevCertFiles.join(','));
      }
      await axios.post(`${API}/buildings/${buildingId}/email-templates/send`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t('emailSent') || 'Email sent');
      // Phase 982 — Email is now persisted server-side (history +
      // logs); drop the local working-draft snapshot.
      clearWorkingDraft();
      setConfirmOpen(false);
      setAttachments([]);
      setSendTo(''); setSendCc(''); setSendBcc('');
      setAttachElev(false); setPeriodFrom(''); setPeriodTo('');
      setAttachAnnual(false);
      setAttachRepairs(false); setRepairsSelectedIds([]);
      setAttachApts(false); setAptPeriodFrom(''); setAptPeriodTo('');
      setResendLogId(''); setResendOriginals([]);
      setSelectedElevCertFiles([]);
      // Phase 355 — Reset the editable draft & touched flag so the next
      // compose pulls fresh subject/body from whichever template the user
      // picks next.
      setDraftTouched(false);
      setDraftSubject(''); setDraftBody('');
      // Phase 351 — Refresh history so the newly-sent row shows up immediately.
      fetchHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('errorOccurred') || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="building-emails-tab">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Envelope size={18} weight="fill" className="text-zinc-700" />
          <h2 className="text-base font-semibold tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>
            {t('emails') || 'Emails'}
          </h2>
          <span className="text-xs text-zinc-500">— {buildingName}</span>
        </div>
      </div>

      {/* Phase 351 — Compose / History view toggle. */}
      <div className="flex border-b border-zinc-200" data-testid="building-emails-view-tabs">
        <button
          type="button"
          onClick={() => setActiveView('compose')}
          className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${activeView === 'compose' ? 'border-b-2 border-zinc-900 text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
          data-testid="building-emails-compose-tab"
        >
          {t('compose') || 'Compose'}
        </button>
        <button
          type="button"
          onClick={() => setActiveView('history')}
          className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors flex items-center gap-1.5 ${activeView === 'history' ? 'border-b-2 border-zinc-900 text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
          data-testid="building-emails-history-tab"
        >
          <ClockCounterClockwise size={14} />
          {t('emailHistory') || 'Email History'}
        </button>
      </div>

      {activeView === 'compose' && (
      <div className="border border-zinc-200 bg-white p-4 space-y-4">
        {/* Phase 982 — Working-draft indicator + Discard button.
            Rendered only when we actually have an in-progress draft
            worth showing to the user. */}
        {hasDraftContent(collectDraftSnapshot()) && (
          <div className="flex items-center justify-between gap-2 -mx-4 -mt-4 mb-2 px-4 py-2 bg-amber-50 border-b border-amber-200" data-testid="building-emails-draft-indicator">
            <div className="text-[11px] text-amber-800 font-semibold uppercase tracking-wider">
              {t('draftAutoSaved') || 'Draft auto-saved · picks up where you left off'}
            </div>
            <button
              type="button"
              onClick={discardBuildingDraft}
              className="text-[11px] font-semibold uppercase tracking-wider text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-1"
              data-testid="building-emails-discard-draft"
            >{t('discardDraft') || 'Discard Draft'}</button>
          </div>
        )}
        {/* Template picker. Phase 369 — Selecting a template always replaces
            Subject + Body. The legacy "Set Active" pinning button has been
            removed (user request). */}
        <div>
          <Label className="text-xs">{t('selectTemplate') || 'Select template'}</Label>
          <div className="flex items-center gap-2 mt-1">
            <select
              value={selectedId}
              onChange={(e) => {
                const newId = e.target.value;
                // Phase 369 — Reset the "touched" guard so the template-sync
                // useEffect always replaces Subject + Body with the newly
                // picked template's content. No extra clicks required.
                setDraftTouched(false);
                setSelectedId(newId);
              }}
              className="flex-1 rounded-none border border-zinc-200 bg-white text-sm px-2 py-2"
              data-testid="building-emails-template-picker"
            >
              <option value="">{t('selectTemplate') || 'Select template…'}</option>
              {/* Phase 960 — Group templates by category. Uses <optgroup> so
                  every dropdown looks the same across browsers with zero
                  extra styling. Old templates with no category fall into
                  "Other". */}
              {(() => {
                const GROUPS = [
                  { key: 'statements', label: t('emailCatStatements') || 'Statements' },
                  { key: 'meetings',   label: t('emailCatMeetings')   || 'Meetings' },
                  { key: 'financial',  label: t('emailCatFinancial')  || 'Financial Information' },
                  { key: 'other',      label: t('emailCatOther')      || 'Other' },
                ];
                const bucket = (tpl) => {
                  const c = (tpl.category || '').toLowerCase();
                  if (c === 'statements' || c === 'meetings' || c === 'financial') return c;
                  return 'other';
                };
                return GROUPS.map(g => {
                  const rows = templates.filter(tpl => bucket(tpl) === g.key);
                  if (rows.length === 0) return null;
                  return (
                    <optgroup key={g.key} label={g.label} data-testid={`building-emails-optgroup-${g.key}`}>
                      {rows.map(tpl => (
                        <option key={tpl.template_id} value={tpl.template_id}>
                          {tpl.is_default ? '★ ' : ''}{tpl.name}
                        </option>
                      ))}
                    </optgroup>
                  );
                });
              })()}
            </select>
          </div>
          {selectedTemplate && (
            <p className="text-[11px] text-zinc-500 mt-1 truncate">
              <span className="font-semibold">{t('subject') || 'Subject'}:</span> {selectedTemplate.subject || '—'}
            </p>
          )}
        </div>

        {/* Recipients. Phase 359 — Each field now has a "Pick" button that
            opens the apartments recipients modal. */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">{t('to') || 'To'} *</Label>
              <button
                type="button"
                onClick={() => openPicker('to')}
                className="text-[11px] underline text-zinc-600 hover:text-zinc-900"
                data-testid="building-emails-pick-to"
              >
                {t('pickFromApartments') || 'Pick from apartments'}
              </button>
            </div>
            <Input
              value={sendTo}
              onChange={(e) => setSendTo(e.target.value)}
              className="mt-1 rounded-none"
              placeholder="alice@example.com, bob@example.com"
              data-testid="building-emails-to"
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">CC</Label>
              <button
                type="button"
                onClick={() => openPicker('cc')}
                className="text-[11px] underline text-zinc-600 hover:text-zinc-900"
                data-testid="building-emails-pick-cc"
              >
                {t('pickFromApartments') || 'Pick from apartments'}
              </button>
            </div>
            <Input
              value={sendCc}
              onChange={(e) => setSendCc(e.target.value)}
              className="mt-1 rounded-none"
              placeholder="cc@example.com"
              data-testid="building-emails-cc"
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">BCC</Label>
              <button
                type="button"
                onClick={() => openPicker('bcc')}
                className="text-[11px] underline text-zinc-600 hover:text-zinc-900"
                data-testid="building-emails-pick-bcc"
              >
                {t('pickFromApartments') || 'Pick from apartments'}
              </button>
            </div>
            <Input
              value={sendBcc}
              onChange={(e) => setSendBcc(e.target.value)}
              className="mt-1 rounded-none"
              placeholder="bcc@example.com"
              data-testid="building-emails-bcc"
            />
          </div>
        </div>

        {/* Phase 368/369 — Subject + Body moved inline (was a separate panel below).
            Auto-fills from the selected template every time the user picks one
            (no Reset button needed). */}
        <div data-testid="building-emails-draft-panel">
          <Label className="text-xs">{t('subject') || 'Subject'}</Label>
          <Input
            value={draftSubject}
            onChange={(e) => { setDraftSubject(e.target.value); setDraftTouched(true); }}
            className="mt-1 rounded-none"
            placeholder={t('subjectPlaceholder') || 'Email subject…'}
            data-testid="building-emails-draft-subject"
          />
        </div>

        <div>
          <Label className="text-xs">{t('body') || 'Body'}</Label>
          <div className="mt-1">
            <RichEmailEditor
              value={draftBody}
              onChange={(html) => {
                setDraftBody(html);
                // Phase 368 — Ignore Quill's mount-time normalize onChange.
                // Only flip `touched` when the user has actually started typing.
                if (draftHydratedRef.current) setDraftTouched(true);
              }}
              placeholder={t('bodyPlaceholder') || 'Email body — placeholders like {{building_name}} are still supported.'}
              minHeight="280px"
              testId="building-emails-draft-body"
            />
          </div>
          <p className="text-[10px] text-zinc-400 mt-1 italic">
            {t('draftHint') || 'Placeholders such as {{building_name}}, {{date_today}}, {{fire_total}} are resolved automatically in the preview step.'}
          </p>
        </div>

        {/* Phase 368 — Attachments collapsed into a single section to save vertical space.
            Click header to expand/collapse. Shows count of active auto-attaches + manual files. */}
        <div className="border border-zinc-200" data-testid="building-emails-attachments-wrap">
          <button
            type="button"
            onClick={() => setAttachmentsOpen(!attachmentsOpen)}
            className="w-full flex items-center justify-between px-3 py-2 bg-zinc-50 hover:bg-zinc-100 transition-colors"
            data-testid="building-emails-attachments-toggle"
          >
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-700 flex items-center gap-2">
              <Paperclip size={12} />
              {t('attachments') || 'Attachments'}
              {(() => {
                const n = (attachments.length || 0) + (attachElev ? 1 : 0) + (attachAnnual ? 1 : 0) + (attachRepairs ? 1 : 0) + (attachApts ? 1 : 0);
                return n > 0 ? <span className="font-mono text-[10px] px-1.5 py-0.5 bg-zinc-900 text-white">{n}</span> : null;
              })()}
            </span>
            <CaretDown size={12} className={`text-zinc-500 transition-transform ${attachmentsOpen ? 'rotate-180' : ''}`} />
          </button>
          {attachmentsOpen && (
          <div className="p-3 space-y-3">

        {/* Attachments. */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-xs">{t('attachments') || 'Attachments'}</Label>
            <label className="text-[11px] underline text-zinc-600 hover:text-zinc-900 cursor-pointer" data-testid="building-emails-attach-btn">
              + {t('addFiles') || 'Add files'}
              <input
                type="file"
                multiple
                className="hidden"
                onChange={(e) => { addAttachments(e.target.files); e.target.value = ''; }}
                data-testid="building-emails-attach-input"
              />
            </label>
          </div>
          {attachments.length === 0 ? (
            <p className="text-[11px] text-zinc-400 italic">{t('noAttachmentsHint') || 'No files attached.'}</p>
          ) : (
            <ul className="border border-zinc-200 divide-y divide-zinc-100 max-h-40 overflow-y-auto" data-testid="building-emails-attach-list">
              {attachments.map((a, idx) => (
                <li key={`${a.name}-${idx}`} className="flex items-center gap-2 px-2 py-1.5 text-xs">
                  <Paperclip size={12} className="text-zinc-500" />
                  <span className="flex-1 truncate" title={a.name}>{a.name}</span>
                  <span className="text-[10px] text-zinc-400 font-mono">{formatBytes(a.size)}</span>
                  <button type="button" onClick={() => removeAttachment(idx)} className="text-zinc-400 hover:text-red-600" data-testid={`building-emails-attach-remove-${idx}`}>
                    <Trash size={12} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Elevator statement auto-attach. */}
        <div className="border-t border-zinc-100 pt-3">
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="building-emails-elev-toggle-wrap">
            <Checkbox
              checked={attachElev}
              onCheckedChange={(v) => setAttachElev(!!v)}
              data-testid="building-emails-elev-toggle"
            />
            <span className="text-sm text-zinc-800 font-medium">
              {t('attachElevatorStatement') || 'Attach elevator statement'}
            </span>
          </label>
          {attachElev && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 pl-7" data-testid="building-emails-elev-period">
              <div>
                <Label className="text-xs">{t('from') || 'From'}</Label>
                <Input
                  type="date"
                  value={periodFrom}
                  onChange={(e) => setPeriodFrom(e.target.value)}
                  className="mt-1 rounded-none"
                  data-testid="building-emails-elev-from"
                />
              </div>
              <div>
                <Label className="text-xs">{t('to') || 'To'}</Label>
                <Input
                  type="date"
                  value={periodTo}
                  onChange={(e) => setPeriodTo(e.target.value)}
                  className="mt-1 rounded-none"
                  data-testid="building-emails-elev-to"
                />
              </div>
              <p className="text-[11px] text-zinc-500 italic md:col-span-2">
                {t('attachElevatorStatementHint') || 'Statement PDF will be generated automatically for the selected period and attached.'}
              </p>
              <div className="md:col-span-2 flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!!attachmentPreviewLoading['elevator-statement'] || !periodFrom || !periodTo}
                  onClick={() => openAttachmentPreview('elevator-statement', { date_from: periodFrom, date_to: periodTo })}
                  className="rounded-none"
                  data-testid="building-emails-elev-preview"
                >
                  <Eye size={14} className="mr-1.5" />
                  {attachmentPreviewLoading['elevator-statement'] ? (t('generating') || 'Generating…') : (t('previewPdf') || 'Preview PDF')}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Phase 352 — Annual Financial Statement auto-attach. */}
        <div className="border-t border-zinc-100 pt-3">
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="building-emails-annual-toggle-wrap">
            <Checkbox
              checked={attachAnnual}
              onCheckedChange={(v) => setAttachAnnual(!!v)}
              data-testid="building-emails-annual-toggle"
            />
            <span className="text-sm text-zinc-800 font-medium">
              {t('attachAnnualFinancial') || 'Attach Annual Financial Statement'}
            </span>
          </label>
          {attachAnnual && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 pl-7" data-testid="building-emails-annual-options">
              <div>
                <Label className="text-xs">{t('year') || 'Year'}</Label>
                <select
                  value={annualYear}
                  onChange={(e) => setAnnualYear(e.target.value)}
                  className="mt-1 w-full rounded-none border border-zinc-200 bg-white text-sm px-2 py-2 h-9"
                  data-testid="building-emails-annual-year"
                >
                  {availableYears.length === 0 && <option value="">—</option>}
                  {availableYears.map(yr => (
                    <option key={yr} value={String(yr)}>{yr}</option>
                  ))}
                </select>
              </div>
              <p className="text-[11px] text-zinc-500 italic md:col-span-2">
                {t('attachAnnualFinancialHint') || 'The annual expense statement PDF will be generated for the selected year and attached.'}
              </p>
              <div className="md:col-span-2 flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!!attachmentPreviewLoading['annual-financial'] || !annualYear}
                  onClick={() => openAttachmentPreview('annual-financial', { year: annualYear })}
                  className="rounded-none"
                  data-testid="building-emails-annual-preview"
                >
                  <Eye size={14} className="mr-1.5" />
                  {attachmentPreviewLoading['annual-financial'] ? (t('generating') || 'Generating…') : (t('previewPdf') || 'Preview PDF')}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Phase 352 — Repairs Statement auto-attach. */}
        <div className="border-t border-zinc-100 pt-3">
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="building-emails-repairs-toggle-wrap">
            <Checkbox
              checked={attachRepairs}
              onCheckedChange={(v) => setAttachRepairs(!!v)}
              data-testid="building-emails-repairs-toggle"
            />
            <span className="text-sm text-zinc-800 font-medium">
              {t('attachRepairsStatement') || 'Attach Repairs Statement'}
            </span>
          </label>
          {attachRepairs && (
            <div className="mt-3 pl-7 space-y-3" data-testid="building-emails-repairs-options">
              {/* Phase 355 — Multi-select repairs list. */}
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs">{t('selectRepairs') || 'Select repairs'}</Label>
                  <div className="flex items-center gap-2 text-[11px]">
                    <button
                      type="button"
                      onClick={() => setRepairsSelectedIds(repairsOptions.map(r => r.repair_id))}
                      className="underline text-zinc-600 hover:text-zinc-900"
                      data-testid="building-emails-repairs-select-all"
                    >
                      {t('selectAll') || 'Select all'}
                    </button>
                    <span className="text-zinc-300">|</span>
                    <button
                      type="button"
                      onClick={() => setRepairsSelectedIds([])}
                      className="underline text-zinc-600 hover:text-zinc-900"
                      data-testid="building-emails-repairs-clear-all"
                    >
                      {t('clearAll') || 'Clear'}
                    </button>
                  </div>
                </div>
                <div
                  className="mt-2 border border-zinc-200 max-h-56 overflow-y-auto divide-y divide-zinc-100"
                  data-testid="building-emails-repairs-list"
                >
                  {repairsOptions.length === 0 && (
                    <div className="px-3 py-3 text-[11px] text-zinc-400 italic">
                      {t('noRepairsAvailable') || 'No shared repairs available for this building.'}
                    </div>
                  )}
                  {repairsOptions.map(rep => {
                    const checked = repairsSelectedIds.includes(rep.repair_id);
                    return (
                      <label
                        key={rep.repair_id}
                        className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-50 select-none"
                        data-testid={`building-emails-repairs-row-${rep.repair_id}`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => {
                            setRepairsSelectedIds(prev => v
                              ? Array.from(new Set([...prev, rep.repair_id]))
                              : prev.filter(id => id !== rep.repair_id));
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-zinc-900 truncate" title={rep.description}>
                            {rep.description || <span className="italic text-zinc-400">{t('noDescription') || '(no description)'}</span>}
                          </div>
                          <div className="text-[10px] text-zinc-500 flex items-center gap-2 mt-0.5">
                            <span>{rep.from_date || '—'}</span>
                            <span className="text-zinc-300">•</span>
                            <span className="font-mono">{(Number(rep.amount) || 0).toLocaleString('el-GR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
                            {rep.status && (
                              <>
                                <span className="text-zinc-300">•</span>
                                <span className="capitalize">{rep.status}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <p className="text-[10px] text-zinc-400 mt-1 italic">
                  {repairsSelectedIds.length === 0
                    ? (t('noRepairSelectedHint') || 'Nothing selected — the PDF will include all shared repairs.')
                    : `${repairsSelectedIds.length} ${t('selected') || 'selected'} / ${repairsOptions.length}`}
                </p>
              </div>
              <div>
                <Label className="text-xs">{t('columns') || 'Columns'}</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2" data-testid="building-emails-repairs-cols">
                  {REPAIR_COL_KEYS.map(key => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer select-none text-xs text-zinc-700" data-testid={`building-emails-repairs-col-${key}`}>
                      <Checkbox
                        checked={repairsCols.includes(key)}
                        onCheckedChange={(v) => {
                          setRepairsCols(prev => v ? Array.from(new Set([...prev, key])) : prev.filter(c => c !== key));
                        }}
                      />
                      <span>{REPAIR_COL_LABELS[key]}</span>
                    </label>
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-zinc-500 italic">
                {t('attachRepairsStatementHint') || 'Distribution per apartment (matches the in-app Repairs distribution table). One or more repairs can be selected; leaving the list empty includes every shared repair.'}
              </p>
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!!attachmentPreviewLoading['repairs-statement'] || repairsCols.length === 0 || repairsSelectedIds.length === 0}
                  onClick={() => openAttachmentPreview('repairs-statement', { repair_ids: repairsSelectedIds.join(','), columns: repairsCols.join(',') })}
                  className="rounded-none"
                  data-testid="building-emails-repairs-preview"
                >
                  <Eye size={14} className="mr-1.5" />
                  {attachmentPreviewLoading['repairs-statement'] ? (t('generating') || 'Generating…') : (t('previewPdf') || 'Preview PDF')}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Phase 352 — Apartments Balances Statement auto-attach. */}
        <div className="border-t border-zinc-100 pt-3">
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="building-emails-apts-toggle-wrap">
            <Checkbox
              checked={attachApts}
              onCheckedChange={(v) => setAttachApts(!!v)}
              data-testid="building-emails-apts-toggle"
            />
            <span className="text-sm text-zinc-800 font-medium">
              {t('attachApartmentsBalances') || 'Attach Apartments Balances Statement'}
            </span>
          </label>
          {attachApts && (
            <div className="mt-3 pl-7 space-y-3" data-testid="building-emails-apts-options">
              {/* Phase 397 — Optional Statement Period filter for the Apartments
                  Balances PDF. Empty fields preserve the legacy "full history"
                  behaviour; selecting either or both scopes the totals AND
                  surfaces a Statement Period header inside the PDF. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t('from') || 'From'}</Label>
                  <Input
                    type="date"
                    value={aptPeriodFrom}
                    onChange={(e) => setAptPeriodFrom(e.target.value)}
                    className="rounded-none h-8 text-xs"
                    data-testid="building-emails-apts-from"
                  />
                </div>
                <div>
                  <Label className="text-xs">{t('to') || 'To'}</Label>
                  <Input
                    type="date"
                    value={aptPeriodTo}
                    onChange={(e) => setAptPeriodTo(e.target.value)}
                    className="rounded-none h-8 text-xs"
                    data-testid="building-emails-apts-to"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">{t('columns') || 'Columns'}</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2" data-testid="building-emails-apts-cols">
                  {APT_COL_KEYS.map(key => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer select-none text-xs text-zinc-700" data-testid={`building-emails-apts-col-${key}`}>
                      <Checkbox
                        checked={aptCols.includes(key)}
                        onCheckedChange={(v) => {
                          setAptCols(prev => v ? Array.from(new Set([...prev, key])) : prev.filter(c => c !== key));
                        }}
                      />
                      <span>{APT_COL_LABELS[key]}</span>
                    </label>
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-zinc-500 italic">
                {t('attachApartmentsBalancesHint') || 'A PDF listing apartments, owners, charges, payments and outstanding balances will be attached.'}
              </p>
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!!attachmentPreviewLoading['apartments-balances'] || aptCols.length === 0}
                  onClick={() => openAttachmentPreview('apartments-balances', { columns: aptCols.join(','), date_from: aptPeriodFrom, date_to: aptPeriodTo })}
                  className="rounded-none"
                  data-testid="building-emails-apts-preview"
                >
                  <Eye size={14} className="mr-1.5" />
                  {attachmentPreviewLoading['apartments-balances'] ? (t('generating') || 'Generating…') : (t('previewPdf') || 'Preview PDF')}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Phase 506 — Elevator Certification attachments picker. Shows the
            flat list of every file uploaded across all certifications for
            this building (each row = one file). User checks specific files
            to include; nothing is auto-attached. */}
        {elevCertFiles.length > 0 && (
          <div className="border-t border-zinc-100 pt-3" data-testid="building-emails-elev-cert-attachments">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                {t('elevatorCertificationAttachments') || 'Elevator Certification Attachments'}
              </Label>
              <span className="text-[10px] text-zinc-500 font-mono">
                {selectedElevCertFiles.length} / {elevCertFiles.length}
              </span>
            </div>
            <div className="max-h-44 overflow-y-auto space-y-1 border border-zinc-200 px-2 py-1.5">
              {elevCertFiles.map(f => {
                const checked = selectedElevCertFiles.includes(f.token);
                return (
                  <label
                    key={f.token}
                    className="flex items-center gap-2 text-[11px] cursor-pointer hover:bg-zinc-50 px-1 py-0.5"
                    data-testid={`elev-cert-att-row-${f.token}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedElevCertFiles([...selectedElevCertFiles, f.token]);
                        else setSelectedElevCertFiles(selectedElevCertFiles.filter(t2 => t2 !== f.token));
                      }}
                      className="rounded-none"
                      data-testid={`elev-cert-att-chk-${f.token}`}
                    />
                    <Paperclip size={11} className="text-zinc-500 flex-shrink-0" />
                    <span className="truncate flex-1" title={f.file_name}>{f.file_name}</span>
                    <span className="text-[10px] text-zinc-400 font-mono flex-shrink-0">
                      {f.certificate_number || f.inspection_date} · {formatBytes(f.size_bytes)}
                    </span>
                    {/* Phase 509 — Per-file preview button (inline modal). */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        previewExistingFile(
                          `${API}/buildings/${buildingId}/elevator-certifications/${f.cert_id}/attachments/${f.token.split(':')[1]}`,
                          f.file_name,
                        );
                      }}
                      className="text-blue-700 hover:text-blue-900 text-[10px] underline ml-1 flex-shrink-0"
                      data-testid={`elev-cert-att-preview-${f.token}`}
                    >
                      {t('preview') || 'preview'}
                    </button>
                  </label>
                );
              })}
            </div>
            <p className="text-[11px] text-zinc-500 italic mt-1">
              {t('elevCertAttachmentsHint') || 'Pick individual files to include. Nothing is attached automatically.'}
            </p>
          </div>
        )}

          </div>
          )}
        </div>
        {/* /Phase 368 collapsible attachments wrapper. */}

        {/* Phase 352 — Resend originals badge. */}
        {resendLogId && (
          <div className="border-t border-zinc-100 pt-3" data-testid="building-emails-resend-originals">
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 px-3 py-2">
              <ArrowsClockwise size={14} className="text-amber-700 mt-0.5" />
              <div className="flex-1">
                <div className="text-xs font-semibold text-amber-900">
                  {t('resendOriginalsTitle') || 'Resending — original attachments will be re-included'}
                </div>
                {resendOriginals.length > 0 ? (
                  <ul className="mt-1 space-y-0.5">
                    {resendOriginals.map((a, idx) => (
                      <li key={`o-${idx}`} className="flex items-center gap-2 text-[11px] text-amber-900">
                        <Paperclip size={11} />
                        <span className="truncate" title={a.filename}>{a.filename}</span>
                        <span className="text-[10px] text-amber-700 font-mono">{formatBytes(a.size_bytes)}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-1 text-[11px] text-amber-800 italic">{t('noOriginalAttachments') || 'No original attachments stored.'}</p>
                )}
              </div>
              <button
                type="button"
                onClick={clearResend}
                className="text-[11px] underline text-amber-900 hover:text-amber-700"
                data-testid="building-emails-resend-clear"
              >
                {t('clearResend') || 'Clear'}
              </button>
            </div>
          </div>
        )}

        {/* Action row. */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100">
          <Button
            onClick={openConfirm}
            disabled={!sendTo.trim() || sending}
            className="rounded-none bg-zinc-900 hover:bg-zinc-800"
            data-testid="building-emails-review-btn"
          >
            <Eye size={14} className="mr-1" />
            {t('reviewAndSend') || 'Review & Send'}
          </Button>
        </div>
      </div>
      )}

      {/* Phase 368 — Email draft moved inline into the compose panel (above).
          The separate panel has been removed to reduce vertical scrolling. */}

      {/* Phase 351 — Email History table. Per-building audit log with
          recipients / date+time / subject / template / attachments + actions
          (View / Resend / Download). */}
      {activeView === 'history' && (
        <div className="border border-zinc-200 bg-white" data-testid="building-emails-history-panel">
          <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50 flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
              {t('emailHistory') || 'Email History'}
            </span>
            <span className="text-[10px] text-zinc-400 font-mono">{filteredHistory.length}</span>
            <div className="ml-auto relative">
              <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                placeholder={t('searchHistoryPh') || 'Search recipient / subject / template…'}
                className="pl-7 rounded-none h-7 text-xs w-72"
                data-testid="building-emails-history-search"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="building-emails-history-table">
              <thead>
                <tr className="bg-white border-b border-zinc-200">
                  <th className="px-3 py-2 text-left text-[10px] text-zinc-400 font-semibold uppercase tracking-wider w-32">{t('date') || 'Date'}</th>
                  <th className="px-3 py-2 text-left text-[10px] text-zinc-400 font-semibold uppercase tracking-wider">{t('to') || 'To'}</th>
                  <th className="px-3 py-2 text-left text-[10px] text-zinc-400 font-semibold uppercase tracking-wider">{t('subject') || 'Subject'}</th>
                  <th className="px-3 py-2 text-left text-[10px] text-zinc-400 font-semibold uppercase tracking-wider w-40">{t('template') || 'Template'}</th>
                  <th className="px-3 py-2 text-center text-[10px] text-zinc-400 font-semibold uppercase tracking-wider w-16">{t('attachments') || 'Files'}</th>
                  <th className="px-3 py-2 text-center text-[10px] text-zinc-400 font-semibold uppercase tracking-wider w-20">{t('status') || 'Status'}</th>
                  <th className="px-3 py-2 text-right text-[10px] text-zinc-400 font-semibold uppercase tracking-wider w-32">{t('actions') || 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-12 text-center text-zinc-400 text-xs italic">
                      {historySearch ? (t('noResults') || 'No results.') : (t('noEmailsYet') || 'No emails sent yet from this building.')}
                    </td>
                  </tr>
                ) : filteredHistory.map((row) => {
                  const attCount = (row.attachments_meta || []).length;
                  const isOk = (row.status || '').toLowerCase() === 'sent';
                  return (
                    <tr key={row.log_id} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`building-emails-history-row-${row.log_id}`}>
                      <td className="px-3 py-2 font-mono text-zinc-700">{formatDateTime(row.sent_at)}</td>
                      <td className="px-3 py-2 text-zinc-700 truncate max-w-[260px]" title={row.to_email || (row.to_list || []).join(', ')}>
                        {(row.to_list && row.to_list.length) ? row.to_list.join(', ') : (row.to_email || '—')}
                      </td>
                      <td className="px-3 py-2 text-zinc-700 truncate max-w-[260px]" title={row.subject || ''}>
                        {row.subject || <span className="text-zinc-400 italic">—</span>}
                      </td>
                      <td className="px-3 py-2 text-zinc-500 truncate max-w-[160px]" title={row.template_name || ''}>
                        {row.template_name || <span className="text-zinc-400 italic">—</span>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {attCount > 0 ? (
                          <span className="inline-flex items-center gap-1 text-zinc-700">
                            <Paperclip size={12} />
                            <span className="font-mono text-[11px]">{attCount}</span>
                          </span>
                        ) : <span className="text-zinc-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 border ${isOk ? 'border-emerald-200 text-emerald-700 bg-emerald-50' : 'border-red-200 text-red-700 bg-red-50'}`}>
                          {(row.status || '—').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openHistoryView(row)}
                            className="p-1 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100"
                            title={t('view') || 'View'}
                            data-testid={`building-emails-history-view-${row.log_id}`}
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            onClick={() => resendFromHistory(row)}
                            className="p-1 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100"
                            title={t('resend') || 'Resend'}
                            data-testid={`building-emails-history-resend-${row.log_id}`}
                          >
                            <ArrowsClockwise size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Phase 351 — History detail dialog. Shows the full subject + body
          of the sent email + downloadable links to every attachment. */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[92vh] flex flex-col p-0" data-testid="building-emails-history-view-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-zinc-200">
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('emailDetails') || 'Email Details'}</DialogTitle>
            <DialogDescription>
              {viewEntry?.sent_at ? formatDateTime(viewEntry.sent_at) : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 text-sm">
            {!viewEntry || viewEntry.loading ? (
              <p className="text-zinc-500 text-center py-12">{t('loading') || 'Loading…'}</p>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t('to') || 'To'}</div>
                    <div className="font-mono text-xs text-zinc-800 break-all">{(viewEntry.to_list || []).join(', ') || viewEntry.to_email || '—'}</div>
                  </div>
                  {(viewEntry.cc_list && viewEntry.cc_list.length > 0) || viewEntry.cc ? (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">CC</div>
                      <div className="font-mono text-xs text-zinc-800 break-all">{(viewEntry.cc_list || []).join(', ') || viewEntry.cc}</div>
                    </div>
                  ) : null}
                  {(viewEntry.bcc_list && viewEntry.bcc_list.length > 0) || viewEntry.bcc ? (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">BCC</div>
                      <div className="font-mono text-xs text-zinc-800 break-all">{(viewEntry.bcc_list || []).join(', ') || viewEntry.bcc}</div>
                    </div>
                  ) : null}
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t('template') || 'Template'}</div>
                    <div className="text-xs text-zinc-800">{viewEntry.template_name || '—'}</div>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{t('subject') || 'Subject'}</div>
                  <div className="text-sm font-semibold text-zinc-900">{viewEntry.subject || <span className="text-zinc-400 italic">—</span>}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{t('body') || 'Body'}</div>
                  <div
                    className="email-preview-body bg-white border border-zinc-200 p-4 text-sm max-h-[55vh] overflow-y-auto"
                    data-testid="building-emails-history-view-body"
                    dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(viewEntry.body_html || viewEntry.body_quill || viewEntry.body || '') }}
                  />
                </div>
                {((viewEntry.attachments_meta || []).length > 0) && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                      {t('attachments') || 'Attachments'} ({viewEntry.attachments_meta.length})
                    </div>
                    <ul className="border border-zinc-200 divide-y divide-zinc-100" data-testid="building-emails-history-view-attachments">
                      {viewEntry.attachments_meta.map((a, idx) => (
                        <li key={`${a.filename}-${idx}`} className="flex items-center gap-2 px-2 py-1.5 text-xs">
                          <Paperclip size={12} className="text-zinc-500" />
                          <a
                            href={`${API}/email-history/${viewEntry.log_id}/attachments/${idx}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 truncate underline text-zinc-800 hover:text-zinc-900"
                            data-testid={`building-emails-history-view-attach-${idx}`}
                          >
                            {a.filename}
                          </a>
                          <span className="text-[10px] text-zinc-400 font-mono">{formatBytes(a.size_bytes)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {viewEntry.error && (
                  <div className="bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{viewEntry.error}</div>
                )}
              </>
            )}
          </div>
          <DialogFooter className="px-6 py-3 border-t border-zinc-200">
            <Button variant="outline" onClick={() => setViewOpen(false)} className="rounded-none" data-testid="building-emails-history-view-close">
              {t('close') || 'Close'}
            </Button>
            {viewEntry && !viewEntry.loading && (
              <Button onClick={() => resendFromHistory(viewEntry)} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="building-emails-history-view-resend">
                <ArrowsClockwise size={14} className="mr-1" />
                {t('resend') || 'Resend'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Final confirm dialog. */}
      <Dialog open={confirmOpen} onOpenChange={(o) => { if (!sending) setConfirmOpen(o); }}>
        <DialogContent className="sm:max-w-3xl max-h-[92vh] flex flex-col p-0" data-testid="building-emails-confirm-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-zinc-200">
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('confirmSendTitle') || 'Final preview'}</DialogTitle>
            <DialogDescription>{t('confirmSendHint') || 'This is exactly what the recipients will receive.'}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 text-sm">
            {confirmLoading ? (
              <p className="text-zinc-500 text-center py-12">{t('loading') || 'Loading…'}</p>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t('to') || 'To'}</div>
                    <div className="font-mono text-xs text-zinc-800 break-all">{sendTo || '—'}</div>
                  </div>
                  {sendCc && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">CC</div>
                      <div className="font-mono text-xs text-zinc-800 break-all">{sendCc}</div>
                    </div>
                  )}
                  {sendBcc && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">BCC</div>
                      <div className="font-mono text-xs text-zinc-800 break-all">{sendBcc}</div>
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{t('subject') || 'Subject'}</div>
                  <div className="text-sm font-semibold text-zinc-900" data-testid="building-emails-confirm-subject">
                    {confirmSubject || <span className="text-zinc-400 italic">—</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{t('body') || 'Body'}</div>
                  <div
                    className="email-preview-body bg-white border border-zinc-200 p-4 text-sm max-h-[55vh] overflow-y-auto"
                    data-testid="building-emails-confirm-body"
                    dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(confirmBodyHtml || confirmBody || '') }}
                  />
                </div>
                {/* Combined attachments list (manual + auto). */}
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                    {t('attachments') || 'Attachments'} ({attachments.length + (attachElev ? 1 : 0) + (attachAnnual ? 1 : 0) + (attachRepairs ? 1 : 0) + (attachApts ? 1 : 0) + (resendLogId ? resendOriginals.length : 0)})
                  </div>
                  <ul className="border border-zinc-200 divide-y divide-zinc-100" data-testid="building-emails-confirm-attachments">
                    {attachments.map((a, idx) => (
                      <li key={`m-${idx}`} className="flex items-center gap-2 px-2 py-1.5 text-xs">
                        <Paperclip size={12} className="text-zinc-500" />
                        <span className="flex-1 truncate" title={a.name}>{a.name}</span>
                        <span className="text-[10px] text-zinc-400 font-mono">{formatBytes(a.size)}</span>
                      </li>
                    ))}
                    {attachElev && (
                      <li className="flex items-center gap-2 px-2 py-1.5 text-xs bg-amber-50" data-testid="building-emails-confirm-elev-row">
                        <Paperclip size={12} className="text-amber-700" />
                        <span className="flex-1 truncate">
                          Elevator_Statement_{periodFrom}_to_{periodTo}.pdf
                        </span>
                        <span className="text-[10px] text-amber-700 font-mono">{t('autoGenerated') || 'auto'}</span>
                      </li>
                    )}
                    {attachAnnual && (
                      <li className="flex items-center gap-2 px-2 py-1.5 text-xs bg-amber-50" data-testid="building-emails-confirm-annual-row">
                        <Paperclip size={12} className="text-amber-700" />
                        <span className="flex-1 truncate">Annual_Financial_{annualYear || 'YYYY'}.pdf</span>
                        <span className="text-[10px] text-amber-700 font-mono">{t('autoGenerated') || 'auto'}</span>
                      </li>
                    )}
                    {attachRepairs && (
                      <li className="flex items-center gap-2 px-2 py-1.5 text-xs bg-amber-50" data-testid="building-emails-confirm-repairs-row">
                        <Paperclip size={12} className="text-amber-700" />
                        <span className="flex-1 truncate">
                          Repairs_Statement_{repairsSelectedIds.length > 0 ? `${repairsSelectedIds.length}_selected` : 'all'}.pdf
                        </span>
                        <span className="text-[10px] text-amber-700 font-mono">{t('autoGenerated') || 'auto'}</span>
                      </li>
                    )}
                    {attachApts && (
                      <li className="flex items-center gap-2 px-2 py-1.5 text-xs bg-amber-50" data-testid="building-emails-confirm-apts-row">
                        <Paperclip size={12} className="text-amber-700" />
                        <span className="flex-1 truncate">Apartments_Balances.pdf</span>
                        <span className="text-[10px] text-amber-700 font-mono">{t('autoGenerated') || 'auto'}</span>
                      </li>
                    )}
                    {resendLogId && resendOriginals.map((a, idx) => (
                      <li key={`r-${idx}`} className="flex items-center gap-2 px-2 py-1.5 text-xs bg-blue-50" data-testid={`building-emails-confirm-resend-row-${idx}`}>
                        <Paperclip size={12} className="text-blue-700" />
                        <span className="flex-1 truncate" title={a.filename}>{a.filename}</span>
                        <span className="text-[10px] text-blue-700 font-mono">{t('resend') || 'resend'}</span>
                      </li>
                    ))}
                    {attachments.length === 0 && !attachElev && !attachAnnual && !attachRepairs && !attachApts && !(resendLogId && resendOriginals.length > 0) && (
                      <li className="px-2 py-2 text-[11px] text-zinc-400 italic">{t('noAttachments') || 'No attachments.'}</li>
                    )}
                  </ul>
                </div>
              </>
            )}
          </div>
          <DialogFooter className="px-6 py-3 border-t border-zinc-200">
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={sending}
              className="rounded-none"
              data-testid="building-emails-confirm-cancel"
            >
              {t('cancel') || 'Cancel'}
            </Button>
            <Button
              onClick={confirmAndSend}
              disabled={sending || confirmLoading}
              className="rounded-none bg-zinc-900 hover:bg-zinc-800"
              data-testid="building-emails-confirm-send"
            >
              <PaperPlaneRight size={14} className="mr-1" />
              {sending ? '…' : (t('confirmSend') || 'Confirm Send')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 359 — Apartments recipients picker (shared modal). */}
      <BuildingRecipientsPicker
        buildingId={buildingId}
        open={pickerOpen}
        target={pickerTarget}
        initialEmails={pickerTarget === 'to' ? sendTo : pickerTarget === 'cc' ? sendCc : sendBcc}
        onClose={() => setPickerOpen(false)}
        onConfirm={onPickerConfirm}
      />

      {/* Phase 511 — Universal in-app PDF/image preview modal.
          Renders the attachment INLINE inside an iframe pointed directly
          at the backend URL (auth via `?token=...`). No blob URLs, no
          popups, no placeholder — the PDF appears as soon as the modal
          opens. Works on desktop browsers + the Capacitor wrapper. */}
      <Dialog open={pdfPreviewOpen} onOpenChange={(o) => {
        setPdfPreviewOpen(o);
        if (!o) {
          // Revoke the blob URL to release memory (auto-revoked after
          // 5 min anyway by pdfUtils, but we clear the src immediately).
          if (pdfPreviewUrl && pdfPreviewUrl.startsWith('blob:')) {
            try { URL.revokeObjectURL(pdfPreviewUrl); } catch (_e) { /* ignore */ }
          }
          setPdfPreviewUrl('');
        }
      }}>
        <DialogContent className="max-w-5xl w-full h-[85vh] flex flex-col p-0" data-testid="pdf-preview-modal">
          <DialogHeader className="px-4 py-3 border-b border-zinc-200 flex flex-row items-center justify-between flex-shrink-0">
            <DialogTitle className="text-sm font-semibold truncate pr-4" data-testid="pdf-preview-title">
              {pdfPreviewTitle || (t('attachment') || 'Attachment')}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 bg-zinc-100 overflow-hidden">
            {pdfPreviewUrl ? (
              <iframe
                src={pdfPreviewUrl}
                title={pdfPreviewTitle}
                className="w-full h-full border-0"
                data-testid="pdf-preview-iframe"
              />
            ) : (
              <div className="flex items-center justify-center h-full text-zinc-500 text-sm">
                {t('loading') || 'Loading…'}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
